import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Custom hook to manage dynamic page titles based on current route
 * Updates document.title automatically when route changes
 */
export const usePageTitle = () => {
  const location = useLocation();

  useEffect(() => {
    // Page title configuration mapping
    const pageTitles = {
      '/': 'Sign In - TriServe Energy',
      '/dashboard': 'Customer Service Portal - TriServe Energy',
      '/login': 'Sign In - TriServe Energy',
      '/accounts': 'Customer Accounts - TriServe Energy',
      '/new-customer': 'New Customer Registration - TriServe Energy',
      '/billing': 'Billing & Payments - TriServe Energy',
      '/move-in': 'Move-In Service - TriServe Energy',
      '/move-out': 'Move-Out Service - TriServe Energy',
      '/change-address': 'Change of Address - TriServe Energy',
      '/service-requests': 'Service Requests - TriServe Energy',
      '/work-orders': 'Work Orders - TriServe Energy',
      '/outage-map': 'Outage Map - TriServe Energy',
      '/customer-lookup': 'Customer Lookup - TriServe Energy',
      '/reports': 'Reports & Analytics - TriServe Energy',
      '/settings': 'System Settings - TriServe Energy',
      '/profile': 'User Profile - TriServe Energy',
      '/help': 'Help & Support - TriServe Energy'
    };

    // Get the current pathname
    const currentPath = location.pathname;

    if (currentPath.startsWith('/accounts/')) {
      document.title = 'Account Details - TriServe Energy';
      updateMetaDescription(currentPath);
      return;
    }
    
    // Find matching title or use default
    const pageTitle = pageTitles[currentPath] || 'TriServe Energy - Customer Service Portal';
    
    // Update document title
    document.title = pageTitle;
    
    // Optional: Update meta description based on page
    updateMetaDescription(currentPath);
    
  }, [location.pathname]);

  // Helper function to update meta description dynamically
  const updateMetaDescription = (path) => {
    const descriptions = {
      '/': 'Sign in to the TriServe Energy customer service portal.',
      '/dashboard': 'Customer Service Representative Portal for managing customer accounts, processing payments, and handling utility service requests.',
      '/login': 'Sign in to the TriServe Energy customer service portal.',
      '/accounts': 'Review Salesforce utility customer accounts and billing-related account details in one place.',
      '/accounts/details': 'Review the full Salesforce account profile returned by the TriServe Auth API.',
      '/new-customer': 'Register new customer accounts with integrated credit verification, payment processing, and service scheduling.',
      '/billing': 'Process customer payments, manage billing accounts, and handle payment plans for TriServe Energy customers.',
      '/move-in': 'Process utility connection requests for customers moving to new addresses.',
      '/move-out': 'Handle utility disconnection requests for customers moving from current addresses.',
      '/service-requests': 'Manage utility service requests, work orders, and customer support tickets.',
      '/outage-map': 'View current power outages and restoration status across the TriServe Energy service area.'
    };

    const description = path.startsWith('/accounts/')
      ? descriptions['/accounts/details']
      : (descriptions[path] || descriptions['/dashboard']);
    
    // Update meta description if it exists
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', description);
    }
  };

  return {
    getCurrentTitle: () => document.title,
    setCustomTitle: (title) => {
      document.title = `${title} - TriServe Energy`;
    }
  };
};

/**
 * Hook specifically for setting page titles with step information
 * Useful for multi-step processes like New Customer Registration
 */
export const useStepTitle = (basePage, currentStep, totalSteps, stepName = '') => {
  useEffect(() => {
    let title = basePage;
    
    if (stepName) {
      title += ` - ${stepName}`;
    } else if (currentStep !== undefined && totalSteps) {
      title += ` (Step ${currentStep + 1} of ${totalSteps})`;
    }
    
    title += ' - TriServe Energy';
    document.title = title;
  }, [basePage, currentStep, totalSteps, stepName]);
};

export default usePageTitle;
